import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import { useCart } from '../../context/CartContext';
import { useNavigate } from 'react-router-dom';
import './Checkout.css';

const SERVICE_ID  = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const PUB_KEY     = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
const TMPL_CUST   = process.env.REACT_APP_EMAILJS_CUSTOMER_TEMPLATE;
const TMPL_ADMIN  = process.env.REACT_APP_EMAILJS_ADMIN_TEMPLATE;
const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL;

export default function Checkout() {
  const { items, total, clearCart } = useCart();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: '', email: '', phone: '', address: '', city: '', postal: '', notes: '',
  });
  
  const [step, setStep] = useState(1); // 1 = form, 2 = success
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  // FREEZE DATA: This captures the order details so they don't disappear when clearCart() runs
  const [confirmedOrder, setConfirmedOrder] = useState({
    summary: '',
    total: '0'
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Calculate current cart details for the form and the "freeze"
  const currentSummary = items.map(i =>
    `${i.brand} ${i.name} (UK ${i.size}) x${i.qty} — R ${(i.price * i.qty).toLocaleString()}`
  ).join('\n');
  const currentTotal = total.toLocaleString();

  if (items.length === 0 && step === 1) {
    return (
      <div className="co">
        <div className="co__empty">
          <h2>Your cart is empty</h2>
          <button onClick={() => navigate('/products')}>Back to Shop</button>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError('');

    // Capture order data immediately
    const orderSnapshot = {
      summary: currentSummary,
      total: currentTotal
    };

    const params = {
      customer_name:    form.name,
      customer_email:   form.email,
      customer_phone:   form.phone,
      customer_address: `${form.address}, ${form.city}, ${form.postal}`,
      order_summary:    orderSnapshot.summary,
      order_total:      orderSnapshot.total,
      notes:            form.notes || 'None',
      to_email:         form.email,
    };

    try {
      // Send both emails
      await emailjs.send(SERVICE_ID, TMPL_CUST, params, PUB_KEY);
      await emailjs.send(SERVICE_ID, TMPL_ADMIN, { ...params, to_email: ADMIN_EMAIL }, PUB_KEY);

      // Save data locally for the Success Screen
      setConfirmedOrder(orderSnapshot);
      setStep(2);
      
      // Clear global cart state
      clearCart();
    } catch (err) {
      setError('Something went wrong sending your order. Please try again.');
      console.error(err);
    }
    setSending(false);
  };

  /* ── SUCCESS VIEW ── */
  if (step === 2) {
    return (
      <div className="co">
        <div className="co__success">
          <div className="co__success-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>
          <h1 className="co__success-title">ORDER RECEIVED</h1>
          <p className="co__success-sub">
            Thanks, {form.name}! We've received your order and sent a confirmation to <strong>{form.email}</strong>.
            Our team will be in touch shortly to arrange delivery.
          </p>
          
          <div className="co__success-summary">
            <p className="co__success-summary-label">Order Details</p>
            {/* Using the frozen summary from local state */}
            <pre className="co__success-pre" style={{ whiteSpace: 'pre-wrap', textAlign: 'left' }}>
              {confirmedOrder.summary}
            </pre>
            <div className="co__success-total">
              Total: <strong>R {confirmedOrder.total}</strong>
            </div>
          </div>
          
          <button className="co__success-btn" onClick={() => navigate('/')}>
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  /* ── FORM VIEW ── */
  return (
    <div className="co">
      <div className="co__inner">

        {/* Left: form */}
        <div className="co__form-col">
          <button className="co__back" onClick={() => navigate(-1)}>
            ← Back
          </button>
          <p className="co__eyebrow">Checkout</p>
          <h1 className="co__title">YOUR<br/>DETAILS</h1>

          {error && <p className="co__error">{error}</p>}

          <form className="co__form" onSubmit={handleSubmit}>
            <div className="co__section-label">Contact Information</div>
            <div className="co__row">
              <div className="co__field">
                <label>Full Name *</label>
                <input required value={form.name}
                  onChange={e => set('name', e.target.value)}
                  placeholder="Luyanda Dlamini" />
              </div>
              <div className="co__field">
                <label>Email *</label>
                <input required type="email" value={form.email}
                  onChange={e => set('email', e.target.value)}
                  placeholder="luyanda@email.com" />
              </div>
            </div>
            <div className="co__field">
              <label>Phone Number *</label>
              <input required type="tel" value={form.phone}
                onChange={e => set('phone', e.target.value)}
                placeholder="+27 71 234 5678" />
            </div>

            <div className="co__section-label" style={{ marginTop: 24 }}>Delivery Address</div>
            <div className="co__field">
              <label>Street Address *</label>
              <input required value={form.address}
                onChange={e => set('address', e.target.value)}
                placeholder="12 Vilakazi Street" />
            </div>
            <div className="co__row">
              <div className="co__field">
                <label>City *</label>
                <input required value={form.city}
                  onChange={e => set('city', e.target.value)}
                  placeholder="Soweto" />
              </div>
              <div className="co__field">
                <label>Postal Code *</label>
                <input required value={form.postal}
                  onChange={e => set('postal', e.target.value)}
                  placeholder="1804" />
              </div>
            </div>
            <div className="co__field">
              <label>Order Notes</label>
              <textarea rows={3} value={form.notes}
                onChange={e => set('notes', e.target.value)}
                placeholder="Any special instructions or requests..." />
            </div>

            <div className="co__notice">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              No payment is taken online. Our team will contact you to confirm your order and arrange payment and delivery.
            </div>

            <button type="submit" className="co__submit" disabled={sending}>
              {sending ? 'Placing Order...' : 'Place Order'}
              {!sending && (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12"/>
                  <polyline points="12 5 19 12 12 19"/>
                </svg>
              )}
            </button>
          </form>
        </div>

        {/* Right: order summary (Visible while filling form) */}
        <div className="co__summary-col">
          <div className="co__summary">
            <p className="co__summary-title">Order Summary</p>

            <div className="co__summary-items">
              {items.map(item => (
                <div key={item.key} className="co__summary-item">
                  <div className="co__summary-item-img">
                    <img src={item.image} alt={item.name} />
                    <span className="co__summary-item-qty">{item.qty}</span>
                  </div>
                  <div className="co__summary-item-info">
                    <p className="co__summary-item-name">{item.name}</p>
                    <p className="co__summary-item-meta">UK {item.size} · {item.brand}</p>
                  </div>
                  <p className="co__summary-item-price">
                    R {(item.price * item.qty).toLocaleString()}
                  </p>
                </div>
              ))}
            </div>

            <div className="co__summary-divider" />

            <div className="co__summary-row">
              <span>Subtotal</span>
              <span>R {currentTotal}</span>
            </div>
            <div className="co__summary-row">
              <span>Delivery</span>
              <span className="co__summary-tbd">Arranged via call</span>
            </div>

            <div className="co__summary-divider" />

            <div className="co__summary-total">
              <span>Total</span>
              <span>R {currentTotal}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}